import { ChevronDownIcon } from '@heroicons/react/24/solid';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { only } from 'styled-breakpoints';
import styled from 'styled-components';

import { SecondaryButton } from '@rebound/design-system/components/Button';
import {
  Option,
  OptionsWrapper,
} from '@rebound/design-system/components/FormHelpers';
import { colorSchemeConfig } from '@rebound/design-system/theme/colorScheme';
import { UserJourneyEnum } from '@rebound/shared-models/retailer/retailer.model';

import { useRetailerColorScheme } from '@modules/retailer/retailer.context';

type ViewMultipleReturnsButtonProps = {
  orderReference: string;
  returnIds: string[];
  type?: ViewMultipleReturnsButtonType;
  userJourney?: UserJourneyEnum;
};

export enum ViewMultipleReturnsButtonType {
  Standard = 'Standard',
  LookupPreview = 'LookupPreview',
}

const ViewMultipleReturnsButton = ({
  orderReference,
  returnIds,
  type = ViewMultipleReturnsButtonType.Standard,
  userJourney = UserJourneyEnum.EMAIL_VERIFICATION,
}: ViewMultipleReturnsButtonProps) => {
  const [returnsListOpened, setReturnsListOpened] = useState(false);
  const showFromLookup = type !== ViewMultipleReturnsButtonType.Standard;
  const selectRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const scheme = useRetailerColorScheme();

  const toggleOptionsOpen = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    setReturnsListOpened((prev) => !prev);
  };

  useEffect(() => {
    if (window) {
      const closeOptionsOnOuterClick = (e: MouseEvent) => {
        e.stopPropagation();
        const currentRef = selectRef.current;
        if (currentRef && !currentRef.contains(e.target as Node)) {
          setReturnsListOpened(false);
        }
      };

      window.addEventListener('click', closeOptionsOnOuterClick);

      return () => {
        window.removeEventListener('click', closeOptionsOnOuterClick);
      };
    }
  }, []);

  return (
    <Wrapper>
      {returnIds.length > 0 ? (
        <ViewReturnButtonsWrapper
          scheme={scheme}
          showFromLookup={showFromLookup}
        >
          <ButtonLink
            href={
              userJourney === UserJourneyEnum.EMAIL_VERIFICATION
                ? `/dashboard/returns?highlightReturn=${returnIds[0]}`
                : `/track-return?orderReference=${returnIds[0]}`
            }
            passHref
          >
            <ViewReturnLink
              as="div"
              scheme={scheme}
              showFromLookup={showFromLookup}
            >
              {t('ordersListing.viewReturnLabel') as string}
            </ViewReturnLink>
          </ButtonLink>
          <ReturnSelectChevronButton
            onClick={toggleOptionsOpen}
            scheme={scheme}
            showFromLookup={showFromLookup}
          >
            <ChevronDownIcon />
          </ReturnSelectChevronButton>
        </ViewReturnButtonsWrapper>
      ) : null}
      <SelectReturnOptionsWrapper opened={returnsListOpened} ref={selectRef}>
        {returnIds.map((returnId) => (
          <Link
            href={
              userJourney === UserJourneyEnum.EMAIL_VERIFICATION
                ? `/dashboard/returns?highlightReturn=${returnId}`
                : `/track-return?orderReference=${returnId}`
            }
            passHref
            key={returnId}
          >
            <SelectReturnOptionLink>
              <Option>
                {`${t(
                  'returnsListing.orderReferenceLabel',
                )} #${orderReference}`}
              </Option>
            </SelectReturnOptionLink>
          </Link>
        ))}
      </SelectReturnOptionsWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  position: relative;
  z-index: 20;
`;

const ViewReturnLink = styled(SecondaryButton)<{
  showFromLookup: boolean;
}>`
  width: ${(props) => props.showFromLookup && '170px'};
  padding: 9px 20px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  background-color: ${(props) =>
    props.showFromLookup && colorSchemeConfig[props.scheme].v700};
  color: ${(props) => props.showFromLookup && props.theme.colors.white};

  &:hover,
  :active {
    background-color: ${(props) =>
      props.showFromLookup && colorSchemeConfig[props.scheme].v800};
  }

  ${only('xs')} {
    padding: 11px 0;
    box-sizing: border-box;
    width: 100%;
  }
`;

const ButtonLink = styled(Link)`
  ${only('xs')} {
    width: 100%;
    height: 100%;
  }
`;

const ViewReturnButtonsWrapper = styled.div<{
  scheme: string;
  showFromLookup: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 0;
  background-color: ${(props) =>
    props.showFromLookup && colorSchemeConfig[props.scheme].v700};
  color: ${(props) => props.showFromLookup && props.theme.colors.white};
  border: none;
  overflow: hidden;
  font-weight: 500;
  border-radius: 6px;

  &:hover,
  &:active {
    opacity: 1;
  }

  ${only('xs')} {
    width: 100%;
    justify-content: space-between;
  }
`;

const SelectReturnOptionsWrapper = styled(OptionsWrapper)`
  right: 0;

  ${only('xs')} {
    width: 100%;
  }
`;

const ReturnSelectChevronButton = styled(SecondaryButton)<{
  showFromLookup: boolean;
}>`
  padding: 12px 11px;
  border-left: 1px solid ${(props) => colorSchemeConfig[props.scheme].v300};
  transition: 0.3s ease;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  background-color: ${(props) =>
    props.showFromLookup && colorSchemeConfig[props.scheme].v700};
  color: ${(props) => props.showFromLookup && props.theme.colors.white};

  &:hover,
  :active {
    background-color: ${(props) =>
      props.showFromLookup && colorSchemeConfig[props.scheme].v800};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SelectReturnOptionLink = styled.div`
  line-height: 1.5;

  ${Option} {
    white-space: nowrap;
  }
`;

export default ViewMultipleReturnsButton;
